import { useCallback, useEffect, useRef, useState, type RefCallback } from 'react';
import { PlayerContext, type PlayerContextValue } from './PlayerContext';
import { useStore } from './hooks/useStore';
import { Poster } from './overlays/Poster';
import { BigPlayButton } from './overlays/BigPlayButton';
import { LoadingSpinner } from './overlays/LoadingSpinner';
import { AccessDenied } from './overlays/AccessDenied';
import { AdultWarning } from './overlays/AdultWarning';
import { Announcement } from './overlays/Announcement';
import { HotkeysOverlay } from './overlays/HotkeysOverlay';
import { PlayButton } from './controls/PlayButton';
import { VolumeControl } from './controls/VolumeControl';
import { FullscreenBtn } from './controls/FullscreenBtn';
import { SettingsPanel } from './controls/SettingsPanel';
import { PluginSlot } from './controls/PluginSlot';
import { Timeline } from './controls/Timeline';
import { TimeDisplay } from './controls/TimeDisplay';
import { ActiveZone } from './controls/ActiveZone';
import { Hotkeys } from './Hotkeys';
import { ControlPanelVisibilityHotkey } from './ControlPanelVisibilityHotkey';
import styles from './ReactPlayerShell.module.css';
import { saveSettings } from '@/core/Config';

interface Props extends PlayerContextValue {
  rootRef: RefCallback<HTMLDivElement>;
}

export function ReactPlayerShell({ rootRef, ...ctx }: Props) {
  const { store, eventBus, capabilities } = ctx;
  const isMobile = capabilities?.isMobile ?? false;
  const isPlaying = useStore(store, useCallback(s => s.isPlaying, []));
  const isFullscreen = useStore(store, useCallback(s => s.isFullscreen, []));
  const isOnline = useStore(store, useCallback(s => s.isOnline, []));
  const isVod = useStore(store, useCallback(s => s.isVod, []));
  const hasAnnouncement = useStore(store, useCallback(s => s.announce !== null, []));
  const showAdultWarning = useStore(store, useCallback(s => s.showAdultWarning, []));
  const showPoster = useStore(store, useCallback(s => Boolean(s.showPoster), []));
  const [isHovered, setIsHovered] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isControlsPinned, setIsControlsPinned] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const innerRootRef = useRef<HTMLDivElement | null>(null);

  // Combine refs
  const setRefs = useCallback((el: HTMLDivElement | null) => {
    innerRootRef.current = el;
    rootRef(el);
  }, [rootRef]);

  // Hover timer
  const resetHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setIsHovered(false);
      hoverTimerRef.current = null;
    }, 2500);
  }, []);

  // Desktop: mouse hover
  const onMouseMove = useCallback(() => {
    if (isMobile) return;
    setIsHovered(true);
    resetHoverTimer();
  }, [isMobile, resetHoverTimer]);

  const onMouseLeave = useCallback(() => {
    if (isMobile) return;
    setIsHovered(false);
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, [isMobile]);

  // Mobile: touch toggle
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-no-toggle]') || target.closest('button')) return;
    const wasHovered = hoverTimerRef.current !== null;
    if (wasHovered) {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
      setIsHovered(false);
    } else {
      setIsHovered(true);
      resetHoverTimer();
    }
  }, [resetHoverTimer]);

  // Double-click for fullscreen (desktop) — avoid controls area
  const onDoubleClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-no-toggle]')) return;
    eventBus.emit('fullscreen:change', !store.getState().isFullscreen);
  }, [eventBus, store]);

  // Source of truth: derive `isFullscreen` from real DOM/video state.
  // Standard Fullscreen API on document OR iOS video native fullscreen.
  const readFullscreenState = useCallback((): boolean => {
    const docFs = document.fullscreenElement != null;
    const webkitDocFs =
      typeof (document as Document & { webkitFullscreenElement?: Element | null })
        .webkitFullscreenElement !== 'undefined' &&
      (document as Document & { webkitFullscreenElement?: Element | null })
        .webkitFullscreenElement != null;
    const video = ctx.getEngine()?.getVideoElement() as
      | (HTMLVideoElement & { webkitDisplayingFullscreen?: boolean })
      | null
      | undefined;
    const webkitVideoFs = !!video?.webkitDisplayingFullscreen;
    return docFs || webkitDocFs || webkitVideoFs;
  }, [ctx]);

  // Sync isFullscreen from actual fullscreenchange / webkit events.
  // Never optimistically set true — `.fullscreen` CSS class follows this state,
  // so a false positive would fake a fullscreen look without the system mode.
  useEffect(() => {
    const onFsChange = () => {
      store.setState({ isFullscreen: readFullscreenState() });
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, [store, readFullscreenState]);

  // iOS WebKit: enter/exit fullscreen on the <video> element itself
  // (only the video can take over the whole screen on iPhone).
  useEffect(() => {
    const onWebkitBegin = () => store.setState({ isFullscreen: true });
    const onWebkitEnd = () => store.setState({ isFullscreen: false });
    const video = ctx.getEngine()?.getVideoElement();
    if (!video) return undefined;
    video.addEventListener('webkitbeginfullscreen', onWebkitBegin);
    video.addEventListener('webkitendfullscreen', onWebkitEnd);
    return () => {
      video.removeEventListener('webkitbeginfullscreen', onWebkitBegin);
      video.removeEventListener('webkitendfullscreen', onWebkitEnd);
    };
  }, [ctx, eventBus, store]);

  // Fullscreen intent handler: request the appropriate native API and
  // let the fullscreenchange/webkit events above update the store.
  // Never set isFullscreen: true optimistically — if no API works, stay false.
  useEffect(() => {
    const unsub = eventBus.on('fullscreen:change', (shouldFs: boolean) => {
      const root = innerRootRef.current;
      if (!root) return;

      if (shouldFs) {
        const rootWithWebkit = root as HTMLElement & {
          webkitRequestFullscreen?: () => void;
        };
        if (typeof root.requestFullscreen === 'function') {
          root.requestFullscreen().catch(() => {
            // Fall through to iOS video API; do not flip state on failure.
          });
          return;
        }
        if (typeof rootWithWebkit.webkitRequestFullscreen === 'function') {
          rootWithWebkit.webkitRequestFullscreen();
          return;
        }
        const video = ctx.getEngine()?.getVideoElement() as
          | (HTMLVideoElement & {
              webkitSupportsFullscreen?: boolean;
              webkitEnterFullscreen?: () => void;
            })
          | null
          | undefined;
        if (
          video &&
          video.webkitSupportsFullscreen !== false &&
          typeof video.webkitEnterFullscreen === 'function'
        ) {
          video.webkitEnterFullscreen();
        }
        // No API available → stay in normal mode; the .fullscreen class
        // must not be applied or we would fake a fullscreen look.
        return;
      }

      // shouldFs === false → exit whatever fullscreen is active
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
        return;
      }
      const webkitDocEl = (document as Document & {
        webkitFullscreenElement?: Element | null;
      }).webkitFullscreenElement;
      if (webkitDocEl) {
        (document as Document & { webkitExitFullscreen?: () => void })
          .webkitExitFullscreen?.();
        return;
      }
      const video = ctx.getEngine()?.getVideoElement() as
        | (HTMLVideoElement & { webkitDisplayingFullscreen?: boolean })
        | null
        | undefined;
      if (video?.webkitDisplayingFullscreen) {
        (video as HTMLVideoElement & { webkitExitFullscreen?: () => void })
          .webkitExitFullscreen?.();
      }
    });
    return unsub;
  }, [eventBus, store, ctx, readFullscreenState]);

  // Bind engine actions (play/pause/volume/quality/muted/seek)
  useEffect(() => {
    const getEngine = ctx.getEngine;
    let prevVolume = store.getState().volume || 0.3;

    const unsubs = [
      eventBus.on('play', () => getEngine()?.play()),
      eventBus.on('pause', () => getEngine()?.pause()),
      eventBus.on('volume:change', (vol) => {
        const engine = getEngine();
        if (engine) {
          const rounded = Math.round(vol * 100) / 100;
          if (rounded > 0) prevVolume = rounded;
          engine.setVolume(rounded);
          store.setState({ volume: rounded, isMuted: rounded === 0 });
        }
      }),
      eventBus.on('muted:change', (muted: boolean) => {
        const engine = getEngine();
        if (engine) {
          const currentVol = store.getState().volume;
          const restoreVol = muted ? 0 : (currentVol || prevVolume || 0);
          engine.setVolume(restoreVol);
          store.setState({
            isMuted: muted,
            ...(!muted && currentVol === 0 ? { volume: prevVolume || 0 } : {}),
          });
        }
      }),
      eventBus.on('quality:change', ({ level }) => {
        getEngine()?.setQuality(level);
        saveSettings({ quality: store.getState().quality });
      }),
      eventBus.on('seek:to', (time: number) => {
        const video = getEngine()?.getVideoElement();
        if (!video) return;
        const stateDuration = store.getState().duration;
        const duration = Number.isFinite(video.duration) && video.duration > 0
          ? video.duration
          : stateDuration;
        const clamped = Math.max(0, duration > 0 ? Math.min(time, duration) : time);
        video.currentTime = clamped;
      }),
    ];
    return () => unsubs.forEach(fn => fn());
  }, [ctx.getEngine, eventBus, store]);

  // Hotkeys (desktop only)
  useEffect(() => {
    if (isMobile) return;
    const root = innerRootRef.current;
    if (!root) return;
    const hotkeys = new Hotkeys(eventBus, store, root);
    const visibilityHotkey = new ControlPanelVisibilityHotkey(eventBus, root);
    return () => {
      hotkeys.destroy();
      visibilityHotkey.destroy();
    };
  }, [isMobile, eventBus, store]);

  useEffect(() => {
    return eventBus.on('controls:visibility:toggle', () => {
      setIsControlsPinned(pinned => !pinned);
    });
  }, [eventBus]);

  // Keep controls visible while settings panel is open
  useEffect(() => {
    const unsubs = [
      eventBus.on('settings:open', () => {
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
          hoverTimerRef.current = null;
        }
        setIsSettingsOpen(true);
      }),
      eventBus.on('settings:close', () => {
        setIsSettingsOpen(false);
        resetHoverTimer();
      }),
    ];
    return () => unsubs.forEach(fn => fn());
  }, [eventBus, resetHoverTimer]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  const suppressPrimaryOverlays = hasAnnouncement || showAdultWarning || showPoster || (!isOnline && !isPlaying);
  const showControls = isHovered || !isPlaying || isSettingsOpen || isControlsPinned;
  // Timeline lives above the control bar and appears/disappears together with it
  const showTimeline = isVod && showControls;

  const rootClassName = [
    styles.root,
    isHovered ? styles.hover : '',
    isFullscreen ? styles.fullscreen : '',
    isMobile ? styles.mobile : '',
  ].filter(Boolean).join(' ');

  return (
    <PlayerContext.Provider value={ctx}>
      <div
        ref={setRefs}
        className={rootClassName}
        data-player-root
        tabIndex={0}
        data-hovered={isHovered || undefined}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        onTouchStart={isMobile ? onTouchStart : undefined}
        onDoubleClick={onDoubleClick}
      >
        {/* Overlays */}
        <Poster />
        {!suppressPrimaryOverlays && <LoadingSpinner />}
        {!suppressPrimaryOverlays && <BigPlayButton />}
        <AccessDenied />
        <AdultWarning />
        <Announcement />
        <HotkeysOverlay />
        <PluginSlot slot="debug-console" />

        {/* Stream info bars (plugin-provided) */}
        <PluginSlot slot="stream-info" />

        {/* Bottom control bar */}
        <div
          className={[
            styles.bottomBar,
            (showControls || showTimeline) ? styles.bottomBarVisible : '',
            showControls ? styles.bottomBarControlsVisible : '',
          ].filter(Boolean).join(' ')}
          data-no-toggle
        >
          {showTimeline ? (
            <div className={styles.timelineRow}>
              <Timeline />
            </div>
          ) : null}
          <div className={`${styles.controls}${showControls ? '' : ` ${styles.controlsHidden}`}`} onClick={e => e.stopPropagation()}>
            <div className={styles.controlsLeft}>
              <ActiveZone testId="active-zone-play"><PlayButton /></ActiveZone>
              <ActiveZone testId="active-zone-volume"><VolumeControl /></ActiveZone>
              {isVod && <TimeDisplay />}
            </div>
            <div className={styles.controlsRight}>
              {!isVod && <div className={styles.compactHeightWrapper}><ActiveZone testId="active-zone-plugin-clips"><PluginSlot slot="clips-button" /></ActiveZone></div>}
              <div className={styles.compactHeightWrapper}><ActiveZone testId="active-zone-plugin-player-in-window"><PluginSlot slot="player-in-window-button" /></ActiveZone></div>
              {!isVod && <div className={styles.compactHeightWrapper}><ActiveZone testId="active-zone-plugin-download"><PluginSlot slot="download-button" /></ActiveZone></div>}
              {!isVod && <div className={styles.compactHeightWrapper}><ActiveZone testId="active-zone-plugin-theatre"><PluginSlot slot="theatre-button" /></ActiveZone></div>}
              {!isVod && <ActiveZone testId="active-zone-settings"><SettingsPanel /></ActiveZone>}
              <ActiveZone testId="active-zone-fullscreen"><FullscreenBtn /></ActiveZone>
            </div>
          </div>
        </div>
      </div>
    </PlayerContext.Provider>
  );
}
